import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import * as StationStateMappingActions from './station-state-mapping.actions';
import { SiteService } from '../../../../shared/service/filter/site.service';
import { LineService } from '../../../../shared/service/line/line.service';
import * as oeeAppReducer from '../../../oee.reducer';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
  GetManyResponseInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as AppActions from '../../../app/actions';
import { StationStateMappingService } from './station-state-mapping.service';
import {
  ActivityCRUDInterface,
  LineCRUDInterface,
  SiteCRUDInterface,
  TaskFilterCRUDInterface,
} from '../../../../shared/component/filter/filter.class';
import { ActivityService } from '../../../../shared/service/filter/activity.service';
import { TaskService } from '../../../../shared/service/filter/task.service';
import { IStationStateMappingRow } from '../../../../view/settings/opc-tag-automation-settings/station-state-mapping/station-state-mapping.model';
import { ResponseArrayInterface } from '../../../../shared/model/interface/generic-api-response.model';
import { LineStationGetOneCRUDDataInterface } from '../../../line-station/line-station.model';
import { LineStationService } from '../../../../shared/service/line-station/line-station.service';

@Injectable()
export class StationStateMappingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: StationStateMappingService,
    private readonly siteService: SiteService,
    private readonly lineService: LineService,
    private readonly stationService: LineStationService,
    private readonly activityService: ActivityService,
    private readonly tasksService: TaskService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.STATION_STATE_MAPPING_DATA_LOADING),
      switchMap((payload: StationStateMappingActions.StationStateMappingLoading) => {
        const andConditions = [];
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (payload.params.siteIds && payload.params.siteIds.length > 0) {
          andConditions.push({ siteId: { $in: payload.params.siteIds } });
        }

        if (payload.params.lineIds && payload.params.lineIds.length > 0) {
          andConditions.push({ lineId: { $in: payload.params.lineIds } });
        }

        if (payload.params?.searchText) {
          andConditions.push({
            $or: [
              {
                lineStopperByPathId: {
                  $cont: `${payload.params?.searchText ?? ''}`,
                },
              },
              {
                state: {
                  $cont: `${payload.params?.searchText ?? ''}`,
                },
              },
              {
                lineExtendedCleanerRuleIds: {
                  $cont: `${payload.params?.searchText ?? ''}`,
                },
              },
              {
                lineExtendedTimeInSeconds: {
                  $cont: `${payload.params?.searchText ?? ''}`,
                },
              },
            ],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => {
            return of(new StationStateMappingActions.StationStateMappingLoaded(response));
          }),
          catchError((errorRes) => {
            return of(new StationStateMappingActions.FetchError(errorRes));
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes));
      }),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.GET_STATION_STATE_MAPPING),
      switchMap((objectData: StationStateMappingActions.GetStationStateMapping) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOneWithDropdowns(objectData.recordId, new HttpParams()).pipe(
          switchMap(
            (response: {
              stationStateMappingRecord: BaseOneResponseInterface<IStationStateMappingRow>;
              lines: GetManyResponseInterface<LineCRUDInterface>;
              sites: SiteCRUDInterface[];
              stations: ResponseArrayInterface<LineStationGetOneCRUDDataInterface>;
              activities: ActivityCRUDInterface[];
              tasks: TaskFilterCRUDInterface[];
              lineTasks: TaskFilterCRUDInterface[];
              extendedTasks: TaskFilterCRUDInterface[];
            }) => {
              return of(
                new StationStateMappingActions.SiteDataLoaded(response.sites),
                new StationStateMappingActions.LineDataCompleted(response.lines),
                new StationStateMappingActions.StationsDataCompleted(response.stations),
                new StationStateMappingActions.ActivitiesDataCompleted(response.activities),
                new StationStateMappingActions.TasksDataCompleted(response.tasks),
                new StationStateMappingActions.LineTasksDataCompleted(response.lineTasks),
                new StationStateMappingActions.ExtendedTasksDataCompleted(response.extendedTasks),
                new StationStateMappingActions.GetStationStateMappingCompleted(response.stationStateMappingRecord),
                new AppActions.HideLoader(),
              );
            },
          ),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.ADD_STATION_STATE_MAPPING),
      switchMap((objectData: StationStateMappingActions.AddStationStateMapping) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.stationStateMapping).pipe(
          switchMap((response: BaseOneResponseInterface<IStationStateMappingRow>) => {
            const actions = [
              new StationStateMappingActions.AddStationStateMappingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ];

            return of(...actions);
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.EDIT_STATION_STATE_MAPPING),
      switchMap((objectData: StationStateMappingActions.EditStationStateMapping) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IStationStateMappingRow>) => {
            const actions = [
              new StationStateMappingActions.EditStationStateMappingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ];

            return of(...actions);
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.DELETE_STATION_STATE_MAPPING),
      switchMap((objectData: StationStateMappingActions.DeleteStationStateMapping) => {
        this.store.dispatch(new AppActions.ShowLoader());
        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);
            return of(
              new StationStateMappingActions.DeleteStationStateMappingCompleted(
                objectData.payload.length > 1,
                response,
              ),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((error) => {
        return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
      }),
    ),
  );

  getSiteData = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.SITES_DATA_LOADING),
      switchMap((objectData: StationStateMappingActions.SiteDataLoading) => {
        const httpParams: HttpParams = new HttpParams().set('limit', 1000);

        return from(this.siteService.getData(httpParams)).pipe(
          switchMap((response: SiteCRUDInterface[]) => {
            return of(new StationStateMappingActions.SiteDataLoaded(response));
          }),
          catchError((errorRes) => {
            return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getLines = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.LINES_DATA_LOADING),
      switchMap((objectData: StationStateMappingActions.LineDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());
        const search = {
          statusId: 1,
          siteId: objectData.siteId,
        };
        const httpParams = new HttpParams()
          .set('s', JSON.stringify(search))
          .set('limit', '1000')
          .set('join', 'lineTypeName');

        return this.lineService.getLines(httpParams).pipe(
          switchMap((response: GetManyResponseInterface<Partial<LineCRUDInterface>>) => {
            return of(new StationStateMappingActions.LineDataCompleted(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getStations = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.STATIONS_DATA_LOADING),
      switchMap((objectData: StationStateMappingActions.StationsDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return from(this.stationService.getLineStationData(objectData.lineId)).pipe(
          switchMap((response: ResponseArrayInterface<LineStationGetOneCRUDDataInterface>) => {
            return of(new StationStateMappingActions.StationsDataCompleted(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getActivities = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.ACTIVITIES_DATA_LOADING),
      switchMap((objectData: StationStateMappingActions.ActivitiesDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());
        const search = {
          active: 1,
          id: { $in: objectData.activityIds },
        };

        const httpParams = new HttpParams().set('s', JSON.stringify(search)).set('limit', '1000');

        return from(this.activityService.getData(httpParams)).pipe(
          switchMap((response: ActivityCRUDInterface[]) => {
            return of(new StationStateMappingActions.ActivitiesDataCompleted(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getTasks = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.TASKS_DATA_LOADING),
      switchMap((objectData: StationStateMappingActions.TasksDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());
        const search = {
          statusId: 1,
          activityId: objectData.activityId,
        };

        const httpParams = new HttpParams().set('s', JSON.stringify(search)).set('limit', '1000');

        return from(this.tasksService.getData(httpParams)).pipe(
          switchMap((response: TaskFilterCRUDInterface[]) => {
            return of(new StationStateMappingActions.TasksDataCompleted(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getLineTasks = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.LINE_TASKS_DATA_LOADING),
      switchMap((objectData: StationStateMappingActions.LineTasksDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());
        const search = {
          statusId: 1,
          activityId: objectData.activityId,
        };

        const httpParams = new HttpParams().set('s', JSON.stringify(search)).set('limit', '1000');

        return from(this.tasksService.getData(httpParams)).pipe(
          switchMap((response: TaskFilterCRUDInterface[]) => {
            return of(new StationStateMappingActions.LineTasksDataCompleted(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getExtendedTasks = createEffect(() =>
    this.actions$.pipe(
      ofType(StationStateMappingActions.ActionTypes.EXTENDED_TASKS_DATA_LOADING),
      switchMap((objectData: StationStateMappingActions.ExtendedTasksDataLoading) => {
        this.store.dispatch(new AppActions.ShowLoader());
        const search = {
          statusId: 1,
          activityId: objectData.activityId,
        };

        const httpParams = new HttpParams().set('s', JSON.stringify(search)).set('limit', '1000');

        return from(this.tasksService.getData(httpParams)).pipe(
          switchMap((response: TaskFilterCRUDInterface[]) => {
            return of(new StationStateMappingActions.ExtendedTasksDataCompleted(response), new AppActions.HideLoader());
          }),
          catchError((error) => {
            return of(new StationStateMappingActions.FetchError(error), new AppActions.HideLoader());
          }),
        );
      }),
      catchError((errorRes) => {
        return of(new StationStateMappingActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );
}
